import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { Result } from '@alpha-edge/shared'
import { PageHeader, DataTable, StatusBadge, TabBar, ColumnSettingsButton, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api, ApiError } from '../../services/api'
import { ResultReviewModal } from './ResultReviewModal'
import { useTableColumns } from '../../hooks/use-table-columns'

type Tab = 'attention' | 'clear' | 'release' | 'all'

const TABS = [
  { key: 'attention' as const, label: 'Needs Attention' },
  { key: 'clear' as const, label: 'Ready to Clear' },
  { key: 'release' as const, label: 'Pending Release' },
  { key: 'all' as const, label: 'All Results' },
]

function formatAge(dateStr: string): string {
  const ms = Date.now() - new Date(dateStr).getTime()
  const min = Math.floor(ms / 60000)
  if (min < 60) return `${min}m`
  const hrs = Math.floor(min / 60)
  if (hrs < 24) return `${hrs}h ${min % 60}m`
  const days = Math.floor(hrs / 24)
  return `${days}d ${hrs % 24}h`
}

function filterResults(results: readonly Result[], tab: Tab): readonly Result[] {
  switch (tab) {
    case 'attention':
      return results.filter((r) => r.status === 'pending' || r.status === 'in_progress')
    case 'clear':
      return results.filter((r) => r.status === 'resulted' || r.status === 'verified')
    case 'release':
      // Phase 5.8.4 -- queue waiting for the second-reviewer release.
      return results.filter((r) => r.status === 'pending_release')
    case 'all':
      return results
  }
}

export function TestingPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const validTabs: readonly Tab[] = ['attention', 'clear', 'release', 'all']
  const initialTab = validTabs.includes(searchParams.get('tab') as Tab) ? (searchParams.get('tab') as Tab) : 'attention'

  const [results, setResults] = useState<readonly Result[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>(initialTab)
  const [selectedResult, setSelectedResult] = useState<Result | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('testing')

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly Result[]>('/results?limit=100')
      .then((res) => {
        if (cancelled) return
        setResults(res.data ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load results')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filtered = [...filterResults(results, tab)].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  )
  const attentionCount = results.filter((r) => r.status === 'pending' || r.status === 'in_progress').length
  const clearCount = results.filter((r) => r.status === 'resulted' || r.status === 'verified').length
  const releaseCount = results.filter((r) => r.status === 'pending_release').length

  const tabsWithCounts = TABS.map((t) => {
    if (t.key === 'attention' && attentionCount > 0) return { ...t, label: `Needs Attention (${attentionCount})` }
    if (t.key === 'clear' && clearCount > 0) return { ...t, label: `Ready to Clear (${clearCount})` }
    if (t.key === 'release' && releaseCount > 0) return { ...t, label: `Pending Release (${releaseCount})` }
    return t
  })

  function openResultModal(result: Result) {
    setSelectedResult(result)
    setModalOpen(true)
  }

  function handleUpdated(updated: Result) {
    setResults((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)))
  }

  const columns: readonly Column<Result>[] = [
    {
      key: 'barcode',
      header: 'Barcode',
      sortable: true,
      filterable: true,
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {r.isCritical && <StatusBadge status="critical" label="CRIT" />}
          <strong>{r.barcode || r.id.slice(0, 8)}</strong>
        </span>
      ),
    },
    { key: 'testType', header: 'Test', sortable: true, filterable: true, render: (r) => r.testType },
    { key: 'status', header: 'Status', sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    { key: 'value', header: 'Value', sortable: true, render: (r) => r.resultValue || '--' },
    {
      key: 'age',
      header: 'Age',
      sortable: true,
      render: (r) => (
        <span style={{ color: r.isCritical ? colors.danger : undefined, fontWeight: r.isCritical ? 600 : 400 }}>
          {formatAge(r.createdAt)}
        </span>
      ),
      sortValue: (r) => new Date(r.createdAt).getTime(),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Testing"
        subtitle={`${results.length} results`}
        actions={<ColumnSettingsButton columns={columns} {...tableColumnProps} />}
      />
      <TabBar
        tabs={tabsWithCounts}
        activeTab={tab}
        onTabChange={(k) => {
          const next = k as Tab
          setTab(next)
          setSearchParams(next === 'attention' ? {} : { tab: next }, { replace: true })
        }}
      />

      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            margin: '0.75rem 0',
          }}
        >
          {error}
        </div>
      )}

      <div style={{ marginTop: '0.75rem' }}>
        {loading ? (
          <div style={{ color: colors.textMuted }}>Loading...</div>
        ) : (
          <DataTable
            columns={columns}
            rows={filtered}
            keyField="id"
            onRowClick={openResultModal}
            emptyMessage={
              tab === 'attention'
                ? 'No results need attention. All caught up.'
                : tab === 'clear'
                  ? 'No results ready to clear.'
                  : 'No results found.'
            }
            {...tableColumnProps}
          />
        )}
      </div>

      <ResultReviewModal
        result={selectedResult}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onUpdated={handleUpdated}
      />
    </div>
  )
}
